<template>
  <div class="main admin-reports-edit">
    <FormWrapper
      ref="editor"
      :action="formAction"
      :method="formMethod"
      :target="formTarget"
    >
      <div class="title-bar">
        <h1 class="mb-0">{{ report.id ? "Edit" : "Add New" }} Report</h1>
        <a class="btn-back" :href="route('admin.reports.index')">
          <span class="icon icon-arrow-left"></span>
          <span class="text">Back to List</span>
        </a>
      </div>

      <div class="row no-gutters py-4">
        <div class="col-12 col-lg-8 col-xl-9">
          <div class="row no-gutters px-3 pl-lg-4 pr-lg-0 pl-xl-5">
            <div class="col-12">
              <transition name="fade">
                <Alert v-if="hasAlert" :alert="alert" class="text-center" />
              </transition>
            </div>

            <div v-if="isCustom" class="col-12">
              <hr />
              <EditorGroupToggle
                label="Report Criteria"
                target="toggleGroupCriteria"
                class="mb-4"
                :collapsed="false"
              />
              <div id="toggleGroupCriteria" class="collapse show row">
                <div class="col-12 col-md-6 col-lg-4">
                  <h3 class="text-primary text-uppercase">
                    <strong>Report Type</strong>
                  </h3>
                  <Field
                    type="select"
                    name="settings[report_type]"
                    help-text="This will determine what kind of data will be included in the report, as well as the available filters and columns."
                    :options="typeOptions"
                    v-model="reportType"
                    validate="required"
                    validate-as="Report Type"
                  />
                </div>
                <div class="d-none d-md-flex col-md-8"></div>
              </div>

              <div v-if="reportType" class="row mt-5">
                <div class="col-12">
                  <h3 class="text-primary text-uppercase">
                    <strong>Filters</strong>
                  </h3>
                  <div
                    v-if="reportFilters.length > 1"
                    class="filters-condition-wrap mb-4"
                  >
                    <span>Records must match</span>
                    <Field
                      type="select"
                      class="d-inline-block mb-0 mx-2"
                      name="settings[filters_condition]"
                      :value="filtersCondition"
                      :options="filterConditionsOptions"
                      :hide-select-empty-option="true"
                    />
                    <span
                      >of the following filters to be included in this
                      report.</span
                    >
                  </div>
                  <input
                    v-else
                    type="hidden"
                    name="settings[filters_condition]"
                    :value="$root.constants.REPORT_FILTER_ALL"
                  />
                  <div
                    v-for="(filter, index) in reportFilters"
                    :key="index"
                    class="row mb-3"
                  >
                    <ReportFilter
                      :filters="filters"
                      :types="filterTypeOptions"
                      :operands="operands"
                      :date-options="dateOptions"
                      :value="filter"
                      :index="index"
                      :picklists="picklists"
                      @remove="removeFilter"
                      @slug-changed="filterSlugChanged"
                    />
                  </div>
                  <div class="row">
                    <div class="col-12 text-center text-xl-left">
                      <a
                        class="btn btn-info"
                        href="#"
                        @click.prevent="addFilter()"
                        >Add Filter</a
                      >
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="reportType" class="row mt-5">
                <div class="col-12">
                  <h3 class="text-primary text-uppercase">
                    <strong>Columns</strong>
                  </h3>
                  <div class="row">
                    <div class="col-12 col-xl-4">
                      <Field
                        type="select"
                        label="Column Type"
                        name="selected_column_type"
                        v-model="selectedColumnType"
                        :options="columnTypeOptions"
                      />
                    </div>
                    <div class="col-12 col-xl-4">
                      <Field
                        type="select"
                        label="Column"
                        name="selected_column"
                        v-model="selectedColumn"
                        :options="columnOptions"
                      />
                    </div>
                    <div v-if="reportColumns.length > 0" class="col-12 mt-3">
                      <Draggable class="report-columns" v-model="reportColumns">
                        <a
                          v-for="(slug, index) in reportColumns"
                          :key="index"
                          class="pill mr-2 mb-2"
                          href="#"
                          @click.prevent="removeColumn(index)"
                        >
                          <span class="text">{{ columnLabel(slug) }}</span>
                          <icon icon="remove" class="icon" />
                          <input
                            type="hidden"
                            name="settings[columns][]"
                            :value="slug"
                          />
                        </a>
                      </Draggable>
                    </div>
                  </div>
                </div>
              </div>

              <hr />
              <EditorGroupToggle
                label="Scheduling"
                target="toggleGroupScheduling"
                class="mb-4"
                :collapsed="false"
              />
              <div id="toggleGroupScheduling" class="collapse show row">
                <div class="col-12 mb-3">
                  <p class="text-center">
                    Use the options below to schedule this report to run
                    automatically on a regular basis (or leave blank to disable
                    scheduling).
                  </p>
                </div>
                <div class="col-12 mb-3 col-md-6 col-xl-4 pr-md-4">
                  <Field
                    type="select"
                    name="settings[frequency][type]"
                    v-model="frequencyType"
                    :options="frequencyTypeOptions"
                    :hideSelectEmptyOption="true"
                  />
                  <input
                    type="hidden"
                    name="is_scheduled"
                    :value="isScheduled ? 1 : 0"
                  />
                </div>
                <div
                  v-if="frequencyValueOptions.length > 0"
                  class="col-12 mb-3 col-md-6 col-xl-4 pr-md-4"
                >
                  <Field
                    type="select"
                    name="settings[frequency][value]"
                    v-model="frequencyValue"
                    :options="frequencyValueOptions"
                    :hideSelectEmptyOption="true"
                  />
                </div>
              </div>
            </div>

            <div class="col-12">
              <hr />
              <EditorGroupToggle
                label="Delivery Settings"
                target="toggleGroupSettings"
                class="mb-4"
                :collapsed="false"
              />
              <div id="toggleGroupSettings" class="collapse show row">
                <div class="col-12 mb-3 col-md-6 col-xl-4 pr-md-4">
                  <Field
                    type="text"
                    name="name"
                    label="Report Name"
                    :value="report.name"
                    validate="required"
                    validate-as="Report Name"
                  />
                </div>
                <div class="col-12 mb-3 col-md-6 col-xl-8 pl-md-4">
                  <Field
                    type="text"
                    name="description"
                    label="Description"
                    :value="report.description"
                  />
                </div>
                <div class="col-12 mb-3">
                  <MultiAssignField
                    type="text"
                    name="recipients"
                    label="Email Recipients"
                    :value="report.recipients"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-4 col-xl-3 form-button-wrapper">
          <div class="sticky-top">
            <div class="row no-gutters text-center">
              <div class="col-12 pb-2">
                <a
                  href="#"
                  @click.prevent="save()"
                  class="btn btn-success w-50"
                >
                  Save Changes
                </a>
              </div>
              <div class="col-12 py-2">
                <a
                  :href="route('admin.reports.index')"
                  class="btn btn-danger w-50"
                  >Cancel & Exit</a
                >
              </div>
              <div class="col-12 py-2">
                <a
                  href="#"
                  @click.prevent="runPreview()"
                  class="btn btn-info w-50"
                  >Preview</a
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </FormWrapper>
  </div>
</template>

<script>
import DisplaysAlerts from "mixins/DisplaysAlerts";
import Draggable from "vuedraggable";
import ReportFilter from "./partials/Filter";

export default {
  mixins: [DisplaysAlerts],
  components: { Draggable, ReportFilter },
  props: {
    report: { type: Object, required: true },
    types: { type: Object, required: true },
    columns: { type: Object, required: true },
    filters: { type: Object, required: true },
    operands: { type: Object, required: true },
    dateOptions: { type: Array, required: true },
  },
  data() {
    return {
      reportType: null,
      reportFilters: [],
      filtersCondition: this.$root.constants.REPORT_FILTER_ALL,
      reportColumns: [],
      selectedColumnType: null,
      selectedColumn: null,
      frequencyType: null,
      frequencyValue: null,
      picklists: {},
      preview: false,
    };
  },
  computed: {
    formAction() {
      if (this.preview) {
        return this.route("admin.reports.preview");
      }
      return !this.report.id
        ? this.route("admin.reports.store")
        : this.route("admin.reports.update", { report: this.report.id });
    },
    formMethod() {
      return !this.report.id || this.preview ? "POST" : "PUT";
    },
    formTarget() {
      return this.preview ? "_blank" : null;
    },
    isCustom() {
      return !this.report.id || !!this.report.is_custom;
    },
    isScheduled() {
      return !!this.frequencyType;
    },
    typeOptions() {
      return Object.keys(this.types).map((classname) => {
        var attrs = this.types[classname];
        return { label: attrs.label, value: classname };
      });
    },
    filterConditionsOptions() {
      return [
        { label: "ALL", value: "ALL" },
        { label: "ANY", value: "ANY" },
      ];
    },
    filterTypeOptions() {
      if (!this.reportType) return [];
      return Object.keys(this.types)
        .filter((classname) => {
          return (
            classname == this.reportType ||
            this.types[this.reportType].supports.includes(classname)
          );
        })
        .map((classname) => {
          var attrs = this.types[classname];
          return { label: attrs.label, value: classname };
        });
    },
    columnTypeOptions() {
      if (!this.reportType) return [];
      return Object.keys(this.types)
        .filter((classname) => {
          return (
            classname == this.reportType ||
            this.types[this.reportType].supports.includes(classname)
          );
        })
        .map((classname) => {
          var attrs = this.types[classname];
          return { label: attrs.label, value: classname };
        });
    },
    columnOptions() {
      if (!this.selectedColumnType) return [];
      var columns = this.columns[this.selectedColumnType];
      return Object.keys(columns)
        .map((slug) => {
          var attrs = columns[slug];
          return { label: attrs.label, value: slug };
        })
        .sort((a, b) => {
          if (a.label == b.label) {
            return 0;
          }
          return a.label < b.label ? -1 : 1;
        });
    },
    frequencyTypeOptions() {
      return [
        { label: "--- Scheduling Disabled ---", value: null },
        { label: "Daily", value: "daily" },
        { label: "Weekly", value: "weekly" },
        { label: "Monthly", value: "monthly" },
      ];
    },
    frequencyValueOptions() {
      if (this.frequencyType == "weekly") {
        return [
          { label: "Sunday", value: 0 },
          { label: "Monday", value: 1 },
          { label: "Tuesday", value: 2 },
          { label: "Wednesday", value: 3 },
          { label: "Thursday", value: 4 },
          { label: "Friday", value: 5 },
          { label: "Saturday", value: 6 },
        ];
      } else if (this.frequencyType == "monthly") {
        return [
          { label: "1st", value: 1 },
          { label: "2nd", value: 2 },
          { label: "3rd", value: 3 },
          { label: "4th", value: 4 },
          { label: "5th", value: 5 },
          { label: "6th", value: 6 },
          { label: "7th", value: 7 },
          { label: "8th", value: 8 },
          { label: "9th", value: 9 },
          { label: "10th", value: 10 },
          { label: "11th", value: 11 },
          { label: "12th", value: 12 },
          { label: "13th", value: 13 },
          { label: "14th", value: 14 },
          { label: "15th", value: 15 },
          { label: "16th", value: 16 },
          { label: "17th", value: 17 },
          { label: "18th", value: 18 },
          { label: "19th", value: 19 },
          { label: "20th", value: 20 },
          { label: "21st", value: 21 },
          { label: "22nd", value: 22 },
          { label: "23rd", value: 23 },
          { label: "24th", value: 24 },
          { label: "25th", value: 25 },
          { label: "26th", value: 26 },
          { label: "27th", value: 27 },
          { label: "28th", value: 28 },
          { label: "Last Day of the Month", value: 29 },
        ];
      } else {
        return [];
      }
    },
  },
  watch: {
    reportType(val) {
      this.reportFilters = [];
      this.reportColumns = [];
    },
    selectedColumn(val) {
      this.addColumn();
    },
  },
  methods: {
    addFilter() {
      this.reportFilters.push({
        type: null,
        slug: null,
        operand: null,
        value: null,
        date_option: null,
      });
    },
    removeFilter(index) {
      this.reportFilters.splice(index, 1);
    },
    filterType(item) {
      var type = "";
      var supportedTypes = this.filterTypeOptions.map((t) => t.value);
      Object.keys(this.filters).forEach((classname) => {
        var filters = this.filters[classname];
        if (
          Object.keys(filters).includes(item) &&
          supportedTypes.includes(classname)
        ) {
          type = classname;
        }
      });
      return type;
    },
    filterSlugChanged(slug, type) {
      if (!_.has(this.picklists, slug)) {
        this.axios
          .get(this.route("admin.reports.picklist"), {
            params: {
              type: type,
              slug: slug,
            },
          })
          .then((response) => {
            var picklists = this.picklists;
            picklists[slug] = response.data;
            this.picklists = {};
            this.picklists = _.cloneDeep(picklists);
          });
      }
    },
    addColumn() {
      if (this.reportColumns.includes(this.selectedColumn)) {
        alert("You have already added the selected column.");
        return;
      } else if (!this.selectedColumn) {
        return;
      }
      this.reportColumns.push(this.selectedColumn);
      this.selectedColumn = null;
    },
    removeColumn(index) {
      this.reportColumns.splice(index, 1);
    },
    columnLabel(item) {
      let label = "";
      Object.keys(this.columns).forEach((classname) => {
        if (_.has(this.columns[classname], item)) {
          label = this.columns[classname][item].label;
        }
      });
      return label;
    },
    save() {
      this.preview = false;
      this.$nextTick(() => {
        this.$refs.editor.submit();
      });
    },
    runPreview() {
      this.preview = true;
      this.$nextTick(() => {
        this.$refs.editor.submit();
      });
    },
  },
  mounted() {
    if (_.isObject(this.report.settings || null)) {
      // prefill report type
      this.reportType = this.report.settings.report_type || null;

      // prefill scheduling
      if (_.has(this.report.settings, "frequency")) {
        this.frequencyType = this.report.settings.frequency.type;
        this.frequencyValue = this.report.settings.frequency.value;
      }

      var filters = (this.report.settings.filters || []).map((filter) => {
        filter.type = this.filterType(filter.slug);
        filter.operand = filter.operand || "=";
        filter.value = filter.value || "";
        filter.date_option = filter.date_option || "custom";
        return filter;
      });

      this.filtersCondition =
        this.report.settings.filters_condition ||
        this.$root.constants.REPORT_FILTER_ALL;

      this.$nextTick(() => {
        // prefill filters
        this.reportFilters = _.cloneDeep(filters);
        // prefill columns
        this.reportColumns = _.clone(this.report.settings.columns || []);
      });
    }
  },
};
</script>

<style lang="scss" scoped>
.filters-condition-wrap {
  position: relative;
  > span {
    position: relative;
    top: 3px;
    font-size: 0.875rem;
  }
  > .element {
    min-width: 50px;
  }
}
</style>